import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vcollectFormsApi, vcollectProjectsApi } from '../../api/vcollect';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';

export const VERSION_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted_for_review: 'Submitted for Review', under_review: 'Under Review', approved: 'Approved',
  published: 'Published', active: 'Active', closed: 'Closed', archived: 'Archived', returned_for_redraft: 'Returned for Redraft',
};

interface CreateFormForm { vcollectProjectId: number; formId: string; title: string; description?: string; }

export function VCollectFormsListPage() {
  const [searchParams] = useSearchParams();
  const preselectedProjectId = searchParams.get('vcollectProjectId');
  const [showCreateForm, setShowCreateForm] = useState(!!preselectedProjectId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset, setValue } = useForm<CreateFormForm>();

  useEffect(() => {
    if (preselectedProjectId) setValue('vcollectProjectId', Number(preselectedProjectId));
  }, [preselectedProjectId, setValue]);

  const { data, isLoading, isError } = useQuery({ queryKey: ['vcollect-forms', preselectedProjectId], queryFn: () => vcollectFormsApi.list(preselectedProjectId ? Number(preselectedProjectId) : undefined) });
  const { data: vcollectProjectsData } = useQuery({ queryKey: ['vcollect-projects'], queryFn: () => vcollectProjectsApi.list() });
  const vcollectProjects = vcollectProjectsData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (input: CreateFormForm) => vcollectFormsApi.create({ ...input, vcollectProjectId: Number(input.vcollectProjectId) }),
    onSuccess: (result) => {
      showSuccess('Form created as Version 1 (Draft).');
      queryClient.invalidateQueries({ queryKey: ['vcollect-forms'] });
      setShowCreateForm(false);
      reset();
      navigate(`/vcollect/forms/${result.data.id}/versions/${result.data.versionId}/build`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const forms = data?.data ?? [];
  const preselectedProject = preselectedProjectId ? vcollectProjects.find((p) => p.id === Number(preselectedProjectId)) : undefined;

  return (
    <div className="page">
      {preselectedProjectId ? (
        <a onClick={() => navigate(`/vcollect/projects/${preselectedProjectId}`)} className="table-link">← {preselectedProject?.name ?? 'Back to Project'}</a>
      ) : null}
      <PageHeader title="VCollect — Forms" description="ODK-compatible data collection forms." />

      <SectionHeader title="Forms" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Form'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          {preselectedProjectId ? (
            <input type="hidden" {...register('vcollectProjectId', { required: true, valueAsNumber: true })} />
          ) : (
            <label>Data Collection Project
              <select {...register('vcollectProjectId', { required: true, valueAsNumber: true })}>
                <option value="">Select…</option>
                {vcollectProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}
          <label>Form ID <input placeholder="e.g. household_needs_assessment" {...register('formId', { required: true })} /></label>
          <label>Title <input placeholder="e.g. Household Needs Assessment" {...register('title', { required: true })} /></label>
          <label>Description <input {...register('description')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create Form</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load forms. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={forms}
          rowKey={(f) => f.id}
          emptyMessage="No forms yet."
          columns={[
            { header: 'Title', render: (f) => <a onClick={() => navigate(`/vcollect/forms/${f.id}`)} className="table-link">{f.title}</a> },
            { header: 'Form ID', render: (f) => f.form_id },
            { header: 'Project', render: (f) => f.vcollect_project_name },
            { header: 'Latest Version', render: (f) => f.latest_version_number ? `v${f.latest_version_number}` : '-' },
            { header: 'Status', render: (f) => f.latest_version_status ? <StatusBadge status={f.latest_version_status} label={VERSION_STATUS_LABELS[f.latest_version_status]} /> : '-' },
          ]}
        />
      )}
    </div>
  );
}
